// components/Features.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Shield, Users, Zap, Cpu, Globe, Lock, Gauge } from 'lucide-react';
import { Feature } from '../types';

interface FeaturesProps {
  features?: Feature[];
}

const Features: React.FC<FeaturesProps> = ({ 
  features = [
    {
      id: 'performance',
      icon: <Gauge className="w-6 h-6" />,
      title: 'Nopea toimitus',
      description: 'Optimoitu tuotanto ja nopeat toimitusajat projekteihin',
      gradient: 'from-blue-500 to-cyan-500',
      delay: 0
    },
    {
      id: 'security',
      icon: <Shield className="w-6 h-6" />,
      title: 'Luotettava ja vastuullinen',
      description: 'Laatu ja asiakastyytyväisyys etusijalla',
      gradient: 'from-purple-500 to-pink-500',
      delay: 100
    },
    {
      id: 'usability',
      icon: <Users className="w-6 h-6" />,
      title: 'Asiakaslähtöinen',
      description: 'Räätälöidyt ratkaisut asiakkaan tarpeisiin',
      gradient: 'from-green-500 to-emerald-500',
      delay: 200
    },
    {
      id: 'technology',
      icon: <Cpu className="w-6 h-6" />,
      title: 'Moderni teknologia',
      description: 'CAD-suunnittelu, pikavalmistus ja nykyaikaiset valmistusmenetelmät',
      gradient: 'from-orange-500 to-red-500',
      delay: 300
    },
    {
      id: 'innovation',
      icon: <Zap className="w-6 h-6" />,
      title: 'Innovatiiviset ratkaisut',
      description: 'Luovia ja tehokkaita ratkaisuja haastaviin projekteihin',
      gradient: 'from-indigo-500 to-purple-500',
      delay: 400
    },
    {
      id: 'global',
      icon: <Globe className="w-6 h-6" />,
      title: 'Monipuolinen kokemus',
      description: 'Kokemusta erilaisista teknisistä projekteista ja käyttökohteista',
      gradient: 'from-teal-500 to-blue-500',
      delay: 500
    }
  ]
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <Rocket className="w-4 h-4" />
            Miksi valita FennoFuture
          </div>
          
          <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Ominaisuudet jotka <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">erottavat</span>
          </h2>
          
          <p className={`text-xl text-gray-600 max-w-3xl mx-auto transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Yhdistämme innovaation ja luotettavuuden tarjotaksemme poikkeuksellisia tuloksia jokaisessa projektissa.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`group relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ 
                transitionDelay: `${feature.delay || index * 100}ms`,
                transform: hoveredCard === feature.id ? 'translateY(-8px)' : 'translateY(0)'
              }}
              onMouseEnter={() => setHoveredCard(feature.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden">
                {/* Full-box color wash on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`} />
                
                {/* Icon Container */}
                <div className="relative z-10 mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                  {feature.title}
                </h3>
                <p className="relative z-10 text-gray-600 leading-relaxed">
                  {feature.description}
                </p>


              </div>

            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;